import * as React from "react";
import { Slider as SliderPrimitive } from "radix-ui";

import { cn } from "~/utils/cn";

interface SliderProps
  extends React.ComponentProps<typeof SliderPrimitive.Root> {
  /** Accessible name per thumb, in value order. */
  thumbLabels?: string[];
}

// Reedy slider: the stock ui primitive renders its thumbs internally, so
// per-thumb aria-labels have to live in this wrapper built on the same parts.
function Slider({
  className,
  defaultValue,
  value,
  thumbLabels,
  ...props
}: SliderProps) {
  const thumbValues = value ?? defaultValue;
  const thumbCount = Array.isArray(thumbValues) ? thumbValues.length : 1;

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      className={cn(
        "relative flex w-full touch-none select-none items-center data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      defaultValue={defaultValue}
      value={value}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          "relative h-2 w-full grow cursor-pointer overflow-hidden rounded-full bg-input",
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className="absolute h-full bg-primary"
        />
      </SliderPrimitive.Track>
      {Array.from({ length: thumbCount }).map((_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          // biome-ignore lint/suspicious/noArrayIndexKey: one thumb per value position
          key={index}
          aria-label={thumbLabels?.[index]}
          className={cn(
            "block h-5 w-5 cursor-pointer rounded-full border-2 border-primary bg-background shadow-lg ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
